import React, { useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { PusherProvider } from './contexts/PusherContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { DreamscapeProvider } from './contexts/DreamscapeContext';
import DreamscapeBackground from './components/DreamscapeBackground';
import { PWAProvider, registerServiceWorker } from './hooks/usePWA';
import { InstallBanner, IOSInstallModal } from './components/PWA';
import UpdateBanner from './components/UpdateBanner';
import ProtectedRoute from './components/ProtectedRoute';
import RootRedirect from './components/RootRedirect';
import Login from './pages/Login';
import Signup from './pages/Signup';
import SignupMethod from './pages/SignupMethod';
import VerifyEmail from './pages/VerifyEmail';
import Dashboard from './pages/Dashboard';
import Chat from './pages/Chat';
import Companions from './pages/Companions';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminUserDetail from './pages/admin/AdminUserDetail';
import AdminConversations from './pages/admin/AdminConversations';
import AdminConversationDetail from './pages/admin/AdminConversationDetail';
import AdminMetrics from './pages/admin/AdminMetrics';
import AdminActivity from './pages/admin/AdminActivity';
import CompanionDetails from './pages/admin/CompanionDetails';
import CompanionName from './pages/companion/CompanionName';
import CompanionGender from './pages/companion/CompanionGender';
import CompanionRelationship from './pages/companion/CompanionRelationship';
import CompanionPersonality from './pages/companion/CompanionPersonality';
import CompanionPreset from './pages/companion/CompanionPreset';
import CompanionAvatar from './pages/companion/CompanionAvatar';
import CompanionReady from './pages/companion/CompanionReady';
import Credits from './pages/Credits';
import Subscription from './pages/Subscription';
import BillingHistory from './pages/BillingHistory';
import PhoneLogin from './pages/PhoneLogin';
import CompleteProfile from './pages/CompleteProfile';
import ChangePassword from './pages/settings/ChangePassword';
import ChangeUsername from './pages/settings/ChangeUsername';
import ChangeLanguage from './pages/settings/ChangeLanguage';
import NotificationSettings from './pages/settings/NotificationSettings';
import ThemeSettings from './pages/settings/ThemeSettings';
import UserPreferences from './pages/settings/UserPreferences';
import CompanionSettings from './pages/settings/CompanionSettings';
import RequireCompanion from './components/RequireCompanion';
import PhoneSettings from './pages/PhoneSettings';
import Terms from './pages/legal/Terms';
import Privacy from './pages/legal/Privacy';
import Guidelines from './pages/legal/Guidelines';
import SmsTerms from './pages/legal/SmsTerms';
import GoogleSuccess from './pages/auth/GoogleSuccess';
import AppleSuccess from './pages/auth/AppleSuccess';

// Layout wrapper component (without providers)
const Layout = () => {
  useEffect(() => {
    // Register service worker on mount
    registerServiceWorker().catch(console.error);
  }, []);

  return (
    <div className="h-full">
      <DreamscapeBackground />
      <Toaster
        position="top-center"
        toastOptions={{
          className: '',
          style: {
            background: 'var(--toast-bg, #1a1a1a)',
            color: 'var(--toast-color, #fff)',
            border: '1px solid var(--toast-border, #2a2a2a)',
          },
        }}
      />
      <UpdateBanner />
      <InstallBanner />
      <IOSInstallModal />
      <Outlet />
    </div>
  );
};

// Create router using React Router v7 syntax
const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <Layout />,
      children: [
        { index: true, element: <RootRedirect /> },
        { path: 'login', element: <Login /> },
        { path: 'signup', element: <SignupMethod /> },
        { path: 'signup/email', element: <Signup /> },
        { path: 'signup/phone', element: <PhoneLogin /> },
        { path: 'verify-email', element: <VerifyEmail /> },
        { path: 'complete-profile', element: <CompleteProfile /> },
        { path: 'auth/google/success', element: <GoogleSuccess /> },
        { path: 'auth/apple/success', element: <AppleSuccess /> },
        { path: 'terms', element: <Terms /> },
        { path: 'privacy', element: <Privacy /> },
        { path: 'sms-terms', element: <SmsTerms /> },
        {
          element: <ProtectedRoute />,
          children: [
            { path: 'companion/name', element: <CompanionName /> },
            { path: 'companion/gender', element: <CompanionGender /> },
            { path: 'companion/relationship', element: <CompanionRelationship /> },
            { path: 'companion/personality', element: <CompanionPersonality /> },
            { path: 'companion/preset', element: <CompanionPreset /> },
            { path: 'companion/avatar', element: <CompanionAvatar /> },
            { path: 'companion/ready', element: <CompanionReady /> },
            { path: 'dashboard', element: <Dashboard /> },
            {
              element: <RequireCompanion />,
              children: [
                { path: 'chat', element: <Chat /> },
                { path: 'chat/:companionId', element: <Chat /> },
                { path: 'companions', element: <Companions /> },
              ],
            },
            { path: 'settings', element: <Settings /> },
            { path: 'settings/password', element: <ChangePassword /> },
            { path: 'settings/username', element: <ChangeUsername /> },
            { path: 'settings/language', element: <ChangeLanguage /> },
            { path: 'settings/notifications', element: <NotificationSettings /> },
            { path: 'settings/theme', element: <ThemeSettings /> },
            { path: 'settings/preferences', element: <UserPreferences /> },
            { path: 'settings/companion/:id', element: <CompanionSettings /> },
            { path: 'settings/phone', element: <PhoneSettings /> },
            { path: 'profile', element: <Profile /> },
            { path: 'guidelines', element: <Guidelines /> },
            { path: 'credits', element: <Credits /> },
            { path: 'subscription', element: <Subscription /> },
            { path: 'billing', element: <BillingHistory /> },
            { path: 'admin', element: <AdminDashboard /> },
            { path: 'admin/users', element: <AdminUsers /> },
            { path: 'admin/users/:userId', element: <AdminUserDetail /> },
            { path: 'admin/companions/:id', element: <CompanionDetails /> },
            { path: 'admin/conversations', element: <AdminConversations /> },
            { path: 'admin/conversations/:conversationId', element: <AdminConversationDetail /> },
            { path: 'admin/metrics', element: <AdminMetrics /> },
            { path: 'admin/activity', element: <AdminActivity /> },
          ],
        },
        { path: '*', element: <Navigate to="/" replace /> },
      ],
    },
  ],
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  },
);

function App() {
  return (
    <DreamscapeProvider>
      <ThemeProvider>
        <AuthProvider>
          <PWAProvider>
            <PusherProvider>
              <RouterProvider router={router} />
            </PusherProvider>
          </PWAProvider>
        </AuthProvider>
      </ThemeProvider>
    </DreamscapeProvider>
  );
}

export default App;
